import {HttpErrorResponse} from '@angular/common/http';
import {Component, Input, OnChanges, SimpleChanges, inject} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {finalize, take} from 'rxjs/operators';
import {MaterialModule} from '../../../../material.module';
import {ISampleMessage} from '../../../../models/chatbot-options.model';
import {ToastService} from '../../../../services/layout/toast.service';
import {ChatbotsService} from '../../../../services/objects/chatbots.service';
import {DisabledButtonReasonComponent} from '../../../shared/disabled-button-reason/disabled-button-reason.component';
import {cleanQuickStartText, quickStartTextRequiredValidator} from './quick-start-message.utils';

@Component({
  selector: 'app-quick-start-editor',
  standalone: true,
  imports: [MaterialModule, ReactiveFormsModule, DisabledButtonReasonComponent],
  templateUrl: './quick-start-editor.component.html',
  styleUrls: ['./quick-start-editor.component.scss']
})
export class QuickStartEditorComponent implements OnChanges {
  private static readonly maxUploadBytes = 2 * 1024 * 1024;

  private readonly fb = inject(FormBuilder);
  private readonly chatbotsService = inject(ChatbotsService);
  private readonly toast = inject(ToastService);
  private readonly uploadingRows = new Set<FormGroup>();

  @Input({required: true}) sampleMessages!: FormArray;
  @Input({required: true}) chatbotId = '';
  @Input() localeLabel = 'Default';
  @Input() allowEmpty = false;
  @Input() allowSuggestions = false;
  @Input() allowGenerativeActions = true;

  ngOnChanges(_changes: SimpleChanges): void {
    if (this.sampleMessages && this.sampleMessages.length === 0 && !this.allowEmpty) {
      this.sampleMessages.push(this.createSampleMessageGroup());
    }
  }

  addSampleMessage(message?: ISampleMessage): void {
    this.sampleMessages.push(this.createSampleMessageGroup(message));
    this.sampleMessages.markAsDirty();
  }

  removeSampleMessage(index: number): void {
    if (!this.allowEmpty && this.sampleMessages.length <= 1) {
      this.toast.showError('At least one Quick Start is required.', 3000);
      return;
    }
    this.sampleMessages.removeAt(index);
    this.sampleMessages.markAsDirty();
  }

  get removeDisabled(): boolean {
    return !this.allowEmpty && this.sampleMessages.length <= 1;
  }

  get removeDisabledReason(): string {
    return this.removeDisabled ? 'At least one Quick Start is required.' : '';
  }

  uploadImage(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    if (file.size > QuickStartEditorComponent.maxUploadBytes) {
      this.toast.showError('Quick Start images must be 2 MB or smaller.');
      return;
    }

    const row = this.row(index);
    if (!row || !this.chatbotId || this.uploadingRows.has(row)) return;

    const expectedChatbotId = this.chatbotId;
    this.uploadingRows.add(row);
    this.chatbotsService.uploadQuickStartImageAsync(expectedChatbotId, file).pipe(
      take(1),
      finalize(() => this.uploadingRows.delete(row))
    ).subscribe({
      next: response => {
        if (this.chatbotId !== expectedChatbotId || this.row(index) !== row) return;
        const control = row.get('imageUrl');
        control?.setValue(response.url);
        control?.markAsDirty();
        control?.markAsTouched();
        this.toast.showSuccess('Quick Start image uploaded.', 3000);
      },
      error: (error: HttpErrorResponse) => {
        const message = typeof error.error?.error === 'string'
          ? error.error.error
          : 'The Quick Start image could not be uploaded.';
        this.toast.showError(message);
      }
    });
  }

  removeImage(index: number): void {
    const control = this.row(index)?.get('imageUrl');
    control?.setValue('');
    control?.markAsDirty();
    control?.markAsTouched();
  }

  isUploading(index: number): boolean {
    const row = this.row(index);
    return !!row && this.uploadingRows.has(row);
  }

  previewUrl(index: number): string | null {
    const value = `${this.row(index)?.get('imageUrl')?.value ?? ''}`.trim();
    if (!value) return null;
    try {
      const url = new URL(value);
      return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
    } catch {
      return null;
    }
  }

  suggestExamples(): void {
    if (!this.allowSuggestions || !this.allowGenerativeActions || !this.chatbotId) return;

    const expectedChatbotId = this.chatbotId;
    const expectedSampleMessages = this.sampleMessages;
    this.chatbotsService.proposeSampleMessagesAsync(expectedChatbotId).pipe(take(1)).subscribe({
      next: messages => {
        if (this.chatbotId !== expectedChatbotId || this.sampleMessages !== expectedSampleMessages) return;
        this.sampleMessages.clear();
        (messages ?? []).forEach(message => this.addSampleMessage(message));
        if (this.sampleMessages.length === 0) this.addSampleMessage();
        this.toast.showSuccess('Quick Start suggestions loaded.', 3000);
      },
      error: () => this.toast.showError('Error loading Quick Start suggestions.', 3000)
    });
  }

  row(index: number): FormGroup | null {
    return this.sampleMessages.at(index) as FormGroup | null;
  }

  private createSampleMessageGroup(message?: ISampleMessage): FormGroup {
    return this.fb.group({
      headline: [cleanQuickStartText(message?.headline), quickStartTextRequiredValidator],
      description: [cleanQuickStartText(message?.description), quickStartTextRequiredValidator],
      imageUrl: [(message?.imageUrl ?? '').trim()],
      imageAlt: [cleanQuickStartText(message?.imageAlt || message?.category)]
    });
  }
}
